import { getProcedureOpacity } from 'features/Procedure/utils/utils'
import * as THREE from 'three'
import { type Vector3 } from 'three'
import type { Vector3Like as Vec3Like } from 'three'
import { getOrComputeProjectedCurve } from '../lib/projected-curve-cache'

const SURFACE_OFFSET = 0.002

/**
 * Projects points onto the body mesh surface so a line follows the body shape (bumps).
 * Casts a ray from each point toward the body center and uses the first hit.
 * Applies a small outward offset so the line sits on the surface without z-fighting.
 */
export const projectPointsOntoBody = (points: Vector3[], bodyMesh: THREE.Object3D): Vector3[] => {
  if (points.length === 0) return []
  const box = new THREE.Box3().setFromObject(bodyMesh)
  const center = box.getCenter(new THREE.Vector3())
  const raycaster = new THREE.Raycaster()
  const direction = new THREE.Vector3()
  const projected: Vector3[] = []
  for (const p of points) {
    direction.copy(center).sub(p).normalize()
    raycaster.set(p.clone(), direction)
    const hits = raycaster.intersectObject(bodyMesh, true)
    if (hits.length > 0) {
      const hit = hits[0]
      const point = hit.point.clone()
      if (hit.face?.normal) {
        const worldNormal = hit.face.normal
          .clone()
          .transformDirection((hit.object as THREE.Mesh).matrixWorld)
        point.add(worldNormal.multiplyScalar(SURFACE_OFFSET))
      }
      projected.push(point)
    } else {
      projected.push(p.clone())
    }
  }
  return projected
}

/** Number of segments used to sample the line and project onto the body surface */
const SURFACE_CURVE_SEGMENTS = 24

/**
 * Default front-face camera position (matches ProcedureInitialState.scene.position).
 * Used as fallback when stored camera is missing (e.g. old drawings loaded after refresh).
 */
const DEFAULT_CAMERA_POSITION = new THREE.Vector3(
  0.3510086203464686,
  44.86891839345276,
  22.64939590113562,
)

/**
 * Projects points onto the body surface as seen from the camera.
 * Casts rays FROM the camera THROUGH each point - the first hit is the visible
 * surface at that view angle. This keeps the line at the correct "height" (e.g.
 * on the nose when drawn there, not jumping to the forehead).
 */
export const projectPointsOntoBodyFromCamera = (
  points: Vector3[],
  bodyMesh: THREE.Object3D,
  cameraPosition: THREE.Vector3,
): Vector3[] => {
  if (points.length === 0) return []
  const raycaster = new THREE.Raycaster()
  const direction = new THREE.Vector3()
  const projected: Vector3[] = []

  for (const p of points) {
    direction.copy(p).sub(cameraPosition).normalize()
    raycaster.set(cameraPosition.clone(), direction)
    const hits = raycaster.intersectObject(bodyMesh, true)
    if (hits.length > 0) {
      const hit = hits[0]
      const point = hit.point.clone()
      if (hit.face?.normal) {
        const worldNormal = hit.face.normal
          .clone()
          .transformDirection((hit.object as THREE.Mesh).matrixWorld)
        point.add(worldNormal.multiplyScalar(SURFACE_OFFSET))
      }
      projected.push(point)
    } else {
      projected.push(p.clone())
    }
  }
  return projected
}

export const createSurfaceFollowingCurve = (
  startPoint: Vector3,
  endPoint: Vector3,
  bodyMesh: THREE.Object3D,
  cameraPosition: THREE.Vector3,
): THREE.Curve<Vector3> => {
  const points: Vector3[] = []
  for (let i = 0; i <= SURFACE_CURVE_SEGMENTS; i++) {
    const t = i / SURFACE_CURVE_SEGMENTS
    points.push(new THREE.Vector3().lerpVectors(startPoint, endPoint, t))
  }
  const projected = projectPointsOntoBodyFromCamera(points, bodyMesh, cameraPosition)
  return new THREE.CatmullRomCurve3(projected, false)
}

export const createBackFaceCullingLineMaterial = (
  color: string,
  isSelected = false,
  isFromSelectedProcedure = false,
  isPrinting = false,
  isPreview = false,
  useDepthTest = false,
  inactiveProcedureOpacity = 0,
) => {
  const baseColor = new THREE.Color(color)
  const emissiveIntensity = isSelected ? 0.3 : 0.1

  return new THREE.MeshStandardMaterial({
    color: baseColor,
    emissive: baseColor,
    emissiveIntensity,
    roughness: 0.4,
    metalness: 0.1,
    side: THREE.FrontSide,
    transparent: true,
    opacity: getProcedureOpacity(
      isFromSelectedProcedure,
      isPrinting,
      isPreview,
      inactiveProcedureOpacity,
    ),
    depthTest: useDepthTest,
    depthWrite: false,
    // Strong polygon offset so the tube’s rim at the body silhouette is fully behind the body
    // and doesn’t show as a thin “shadow” line where the curve goes into the invisible area
    polygonOffset: useDepthTest,
    polygonOffsetFactor: useDepthTest ? -12 : 0,
    polygonOffsetUnits: useDepthTest ? -12 : 0,
  })
}

export const getCacheKey = (
  startPoint: Vector3,
  endPoint: Vector3,
  cameraPosition?: THREE.Vector3,
  hasBodyMesh?: boolean,
) => {
  const data = [
    startPoint.x.toFixed(3),
    startPoint.y.toFixed(3),
    startPoint.z.toFixed(3),
    endPoint.x.toFixed(3),
    endPoint.y.toFixed(3),
    endPoint.z.toFixed(3),
    hasBodyMesh ? '1' : '0',
  ]
  if (cameraPosition) {
    data.push(cameraPosition.x.toFixed(3), cameraPosition.y.toFixed(3), cameraPosition.z.toFixed(3))
  }
  return data.join('-')
}

export const createCurve = (
  startPoint: Vector3,
  endPoint: Vector3,
  bodyMesh?: THREE.Object3D,
  cameraPosition?: THREE.Vector3,
): THREE.Curve<Vector3> => {
  if (bodyMesh) {
    const camera = cameraPosition ?? DEFAULT_CAMERA_POSITION
    return createSurfaceFollowingCurve(startPoint, endPoint, bodyMesh, camera)
  }
  return new THREE.LineCurve3(startPoint, endPoint)
}

export const createSurfaceFollowingCurveFromPoints = (
  points: Vector3[],
  bodyMesh?: THREE.Object3D,
  cameraPosition?: THREE.Vector3,
): THREE.Curve<Vector3> => {
  if (points.length < 2) {
    return new THREE.CatmullRomCurve3(points.length > 0 ? points : [new THREE.Vector3()], false)
  }
  if (bodyMesh) {
    const camera = cameraPosition ?? DEFAULT_CAMERA_POSITION
    const projected = projectPointsOntoBodyFromCamera(points, bodyMesh, camera)
    return new THREE.CatmullRomCurve3(projected, false)
  }
  return new THREE.CatmullRomCurve3(points, false)
}

export const truncateCurveForArrow = (
  curve: THREE.Curve<Vector3>,
  lineWidth: number,
): THREE.Curve<Vector3> => {
  const arrowSpace = (lineWidth / 100) * 8
  const totalLength = curve.getLength()
  const truncateAt = Math.max(0, 1 - arrowSpace / totalLength)

  const points = []
  const segments = 64
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * truncateAt
    points.push(curve.getPoint(t))
  }
  return new THREE.CatmullRomCurve3(points)
}

export const truncateCurveForArrows = (
  curve: THREE.Curve<Vector3>,
  lineWidth: number,
): THREE.Curve<Vector3> => {
  const arrowSpace = (lineWidth / 100) * 8
  const totalLength = curve.getLength()
  const truncateStart = Math.min(1, arrowSpace / totalLength)
  const truncateEnd = Math.max(0, 1 - arrowSpace / totalLength)

  const points = []
  const segments = 64
  for (let i = 0; i <= segments; i++) {
    const t = truncateStart + (i / segments) * (truncateEnd - truncateStart)
    points.push(curve.getPoint(t))
  }
  return new THREE.CatmullRomCurve3(points)
}

export const createLineGeometry = (
  curve: THREE.Curve<Vector3>,
  lineWidth: number,
  showDirection: boolean,
  showMeasurement?: boolean,
): THREE.TubeGeometry => {
  let displayCurve = curve
  if (showMeasurement) {
    displayCurve = truncateCurveForArrows(curve, lineWidth)
  } else if (showDirection) {
    displayCurve = truncateCurveForArrow(curve, lineWidth)
  }
  return new THREE.TubeGeometry(displayCurve, 64, lineWidth / 100, 8, false)
}

export const createPickGeometry = (
  curve: THREE.Curve<Vector3>,
  lineWidth: number,
  showDirection: boolean,
  showMeasurement?: boolean,
): THREE.TubeGeometry => {
  const pickWidth = lineWidth * 4
  let displayCurve = curve
  if (showMeasurement) {
    displayCurve = truncateCurveForArrows(curve, lineWidth)
  } else if (showDirection) {
    displayCurve = truncateCurveForArrow(curve, lineWidth)
  }
  return new THREE.TubeGeometry(displayCurve, 64, pickWidth / 100, 8, false)
}

export const calculateArrowPositionAndDirection = (
  curve: THREE.Curve<Vector3>,
  lineWidth: number,
): { position: Vector3; direction: Vector3; arrowLength: number } => {
  const lineRadius = lineWidth / 100
  const arrowLength = lineRadius * 8

  const position = curve.getPointAt(1.0)
  const direction = curve.getTangentAt(1.0).normalize()

  return { position, direction, arrowLength }
}

export const calculateArrowPositionsAndDirections = (
  curve: THREE.Curve<Vector3>,
  lineWidth: number,
): {
  start: { position: Vector3; direction: Vector3; arrowLength: number }
  end: { position: Vector3; direction: Vector3; arrowLength: number }
} => {
  const lineRadius = lineWidth / 100
  const arrowLength = lineRadius * 8

  const startPosition = curve.getPointAt(0.0)
  const startDirection = curve.getTangentAt(0.0).normalize().negate()

  const endPosition = curve.getPointAt(1.0)
  const endDirection = curve.getTangentAt(1.0).normalize()

  return {
    start: { position: startPosition, direction: startDirection, arrowLength },
    end: { position: endPosition, direction: endDirection, arrowLength },
  }
}

const bodyBBoxCache = new WeakMap<THREE.Object3D, { box: THREE.Box3; center: THREE.Vector3 }>()

const getBodyCenter = (bodyMesh: THREE.Object3D): THREE.Vector3 => {
  let cached = bodyBBoxCache.get(bodyMesh)
  if (!cached) {
    const box = new THREE.Box3().setFromObject(bodyMesh)
    const center = box.getCenter(new THREE.Vector3())
    cached = { box, center }
    bodyBBoxCache.set(bodyMesh, cached)
  }
  return cached.center
}

export const getSurfaceNormalAtPoint = (
  bodyMesh: THREE.Object3D,
  point: Vector3,
): THREE.Vector3 => {
  const center = getBodyCenter(bodyMesh)
  const outward = new THREE.Vector3().subVectors(point, center).normalize()
  const castFrom = point.clone().addScaledVector(outward, 0.5)
  const raycaster = new THREE.Raycaster()
  raycaster.set(castFrom, outward.clone().negate())
  const hits = raycaster.intersectObject(bodyMesh, true)
  if (hits.length > 0 && hits[0].face?.normal) {
    return hits[0].face.normal
      .clone()
      .transformDirection((hits[0].object as THREE.Mesh).matrixWorld)
  }
  return new THREE.Vector3(0, 1, 0)
}

/**
 * Normal of the skin face that was visible from `cameraPosition` at `surfacePoint`.
 * Casts camera → surfacePoint and, among all hits, picks the intersection closest to
 * `surfacePoint`. Matches the face the user hit when placing the marker; avoids wrong
 * normals in hollows (eye socket, nostrils) where body-center–based rays hit interior
 * geometry and orient a flat X edge-on so one arm disappears.
 */
export const getSurfaceNormalFromViewRay = (
  bodyMesh: THREE.Object3D,
  surfacePoint: Vector3,
  cameraPosition: Vector3,
): THREE.Vector3 | null => {
  const dir = new THREE.Vector3().subVectors(surfacePoint, cameraPosition)
  if (dir.lengthSq() < 1e-12) return null
  dir.normalize()
  const raycaster = new THREE.Raycaster()
  raycaster.set(cameraPosition.clone(), dir)
  const hits = raycaster.intersectObject(bodyMesh, true)
  if (hits.length === 0) return null

  let best = hits[0]
  let bestD = best.point.distanceToSquared(surfacePoint)
  for (let i = 1; i < hits.length; i++) {
    const d = hits[i].point.distanceToSquared(surfacePoint)
    if (d < bestD) {
      bestD = d
      best = hits[i]
    }
  }
  if (!best.face?.normal) return null
  return best.face.normal.clone().transformDirection((best.object as THREE.Mesh).matrixWorld)
}

const snapArmEndpointToSurface = (
  point: Vector3,
  bodyMesh: THREE.Object3D,
  fallbackNormal: THREE.Vector3,
  cameraPosition?: THREE.Vector3,
): Vector3 => {
  const SURFACE_OFFSET = 0.05

  if (cameraPosition) {
    const dir = new THREE.Vector3().subVectors(point, cameraPosition).normalize()
    const raycaster = new THREE.Raycaster()
    raycaster.set(cameraPosition.clone(), dir)
    const hits = raycaster.intersectObject(bodyMesh, true)
    if (hits.length > 0) {
      const hit = hits[0]
      const hitNormal = hit.face?.normal
        ? hit.face.normal.clone().transformDirection((hit.object as THREE.Mesh).matrixWorld)
        : fallbackNormal
      return hit.point.clone().add(hitNormal.clone().multiplyScalar(SURFACE_OFFSET))
    }
  }

  // Fallback: push the tangent-plane point outward along the center normal
  return point.clone().add(fallbackNormal.clone().multiplyScalar(SURFACE_OFFSET))
}

/**
 * Creates two curves for an X shape on the body surface.
 * The X is drawn in the camera-facing plane so it appears as a proper symmetric X
 * from the user's viewpoint (avoids distortion on curved surfaces like arms).
 * Each arm endpoint is snapped to the mesh surface via a camera raycast so that
 * the arms remain fully visible even on concave/curved regions like the face.
 */
export const createXCurves = (
  center: Vector3,
  size: number,
  bodyMesh?: THREE.Object3D,
  cameraPosition?: THREE.Vector3,
): [THREE.Curve<Vector3>, THREE.Curve<Vector3>] => {
  const camPos = cameraPosition ?? DEFAULT_CAMERA_POSITION
  const viewDir = new THREE.Vector3().subVectors(center, camPos).normalize()
  const worldUp = new THREE.Vector3(0, 1, 0)
  const tangent1 = new THREE.Vector3().crossVectors(viewDir, worldUp).normalize()
  if (tangent1.lengthSq() < 0.01) {
    tangent1.crossVectors(viewDir, new THREE.Vector3(1, 0, 0)).normalize()
  }
  const tangent2 = new THREE.Vector3().crossVectors(tangent1, viewDir).normalize()

  const s = size / Math.SQRT2

  const rawD1Start = center
    .clone()
    .add(tangent1.clone().multiplyScalar(-s))
    .add(tangent2.clone().multiplyScalar(-s))
  const rawD1End = center
    .clone()
    .add(tangent1.clone().multiplyScalar(s))
    .add(tangent2.clone().multiplyScalar(s))
  const rawD2Start = center
    .clone()
    .add(tangent1.clone().multiplyScalar(-s))
    .add(tangent2.clone().multiplyScalar(s))
  const rawD2End = center
    .clone()
    .add(tangent1.clone().multiplyScalar(s))
    .add(tangent2.clone().multiplyScalar(-s))

  const fallbackNormal = bodyMesh
    ? getSurfaceNormalAtPoint(bodyMesh, center)
    : new THREE.Vector3(0, 1, 0)

  if (bodyMesh) {
    const snap = (p: Vector3) => snapArmEndpointToSurface(p, bodyMesh, fallbackNormal, camPos)
    return [
      new THREE.LineCurve3(snap(rawD1Start), snap(rawD1End)),
      new THREE.LineCurve3(snap(rawD2Start), snap(rawD2End)),
    ]
  }

  return [new THREE.LineCurve3(rawD1Start, rawD1End), new THREE.LineCurve3(rawD2Start, rawD2End)]
}

/**
 * Memoized variant of `createSurfaceFollowingCurveFromPoints`, keyed on the
 * stored `storedPoints` array reference. Pass the raw points-from-state array
 * (not a Vector3-mapped copy) so the WeakMap key is stable across renders.
 *
 * `cameraPosition` is still forwarded to the underlying builder for the
 * first-time projection (it falls back to `DEFAULT_CAMERA_POSITION` when
 * missing), but it is NOT part of the cache key — re-projecting on every
 * orbit is the original bug this helper fixes.
 */
export const getCachedSurfaceFollowingCurve = (
  storedPoints: readonly Vec3Like[],
  vectorPoints: Vector3[],
  bodyMesh?: THREE.Object3D,
  cameraPosition?: THREE.Vector3,
): THREE.Curve<Vector3> =>
  getOrComputeProjectedCurve(storedPoints, bodyMesh, () =>
    createSurfaceFollowingCurveFromPoints(vectorPoints, bodyMesh, cameraPosition),
  )
